// 身份验证工具
import axios from 'axios';
import { API_BASE_URL, DEFAULT_USER } from '../config/api';

// ================================
// 配置
// ================================
const TOKEN_KEY = 'access_token';
const METHOD_KEY = 'auth_method';
const USER_KEY = 'current_user';

// 用于通知应用层认证失败的回调
let onAuthFailedCallback = null;

// 设置认证失败回调
export const setOnAuthFailed = (callback) => {
  onAuthFailedCallback = callback;
};

// ================================
// Axios 响应拦截器 - 全局处理401错误
// ================================
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // 如果是401错误,说明认证失败
    if (error.response && error.response.status === 401) {
      console.warn('⚠️ 检测到401错误,认证令牌无效或已过期');
      
      // 清除本地的认证信息
      logout();
      
      // 触发认证失败回调,通知App组件
      if (onAuthFailedCallback) {
        onAuthFailedCallback({
          error: error.response.data?.detail || '认证令牌已失效,请重新登录',
          status: 401
        });
      }
    }
    
    return Promise.reject(error);
  }
);

// ================================
// 基础 Token 操作
// ================================
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    removeAuthToken();
  }
};

export const removeAuthToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

export const isAuthenticated = () => !!getAuthToken();

// ================================
// 登录逻辑
// ================================
export const login = async (username, password) => {
  // 验证必填参数
  if (!username || !password) {
    return {
      success: false,
      error: '请输入用户名和密码'
    };
  }
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      username,
      password
    });

    const token = response.data.access_token || response.data.token;
    if (token) {
      setAuthToken(token);
      localStorage.setItem(METHOD_KEY, 'bearer');
      if (response.data.user) setCurrentUser(response.data.user);
      return { success: true, token, user: response.data.user };
    }

    return { success: false, error: '登录响应格式错误' };

  } catch (error) {
    console.error('❌ 登录失败:', error);

    return {
      success: false,
      error: error.response?.data?.detail || error.message || '登录失败'
    };
  }
};



// ================================
// 自动登录（页面加载时调用）
// ================================
export const autoLogin = async () => {
  const token = getAuthToken();
  const method = localStorage.getItem(METHOD_KEY);

  if (token && method) {
    // 恢复 Bearer Token 认证头
    if (method === 'bearer') {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // 不再在这里验证，由后续的实际 API 请求来验证
      return { success: true, method };
    }
  }

  // 无有效认证则返回失败，要求用户登录
  return { success: false, error: '未登录，请先登录' };
};

// ================================
// 登出
// ================================
export const logout = () => {
  removeAuthToken();
  localStorage.removeItem(METHOD_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('family_id'); // 同时清除family_id
};

// ================================
// 用户信息存取
// ================================
export const getCurrentUser = () => {
  const saved = localStorage.getItem(USER_KEY);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (err) {
      console.error('解析用户信息失败:', err);
    }
  }
  return DEFAULT_USER;
};

export const setCurrentUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
